/**
 * A section's ⋯ menu (#2291): a button that opens a short list of actions.
 * Arrow keys move between them, Escape or a click elsewhere closes it, and
 * focus goes back to the button. An action with `checked` is one of a set of
 * choices (a radio item, #2302), and one with `divided` starts a new group.
 */
import { Fragment, useEffect, useId, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faEllipsis } from '@fortawesome/pro-solid-svg-icons';

export interface MenuAction {
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  /** One of a set of choices: whether it is the one chosen. */
  checked?: boolean;
  /** A line before it: the start of another group. */
  divided?: boolean;
}

export default function SectionMenu({ label, actions }: Readonly<{ label: string; actions: MenuAction[] }>) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const items = useRef<(HTMLButtonElement | null)[]>([]);

  // A click outside closes it (a document listener: the menu is open).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    items.current.find((b) => b && !b.disabled)?.focus();
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const close = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };

  const move = (dir: 1 | -1) => {
    const enabled = items.current.filter((b): b is HTMLButtonElement => !!b && !b.disabled);
    if (!enabled.length) return;
    const at = enabled.indexOf(document.activeElement as HTMLButtonElement);
    enabled[(at + dir + enabled.length) % enabled.length].focus();
  };

  return (
    <div ref={rootRef} className="edit-menu">
      <button
        ref={buttonRef}
        type="button"
        className="edit-shelf-btn"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <FontAwesomeIcon icon={faEllipsis} className="text-[12px]"/>
      </button>
      {open && (
        <div
          id={menuId}
          role="menu"
          tabIndex={-1}
          aria-label={label}
          className="edit-menu-list"
          onKeyDown={(e) => {
            if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
            else if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
            else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
            else if (e.key === 'Tab') setOpen(false);
          }}
        >
          {actions.map((a, i) => (
            <Fragment key={a.label}>
              {a.divided && <hr className="edit-menu-sep"/>}
              <button
                ref={(el) => { items.current[i] = el; }}
                type="button"
                role={a.checked === undefined ? 'menuitem' : 'menuitemradio'}
                aria-checked={a.checked}
                className="edit-menu-item"
                disabled={a.disabled}
                onClick={() => { close(); a.onSelect(); }}
              >
                {a.label}
                {a.checked && <FontAwesomeIcon icon={faCheck} className="edit-menu-check" aria-hidden="true"/>}
              </button>
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
